import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import {
  type CreateUserRequest,
  type UpdateUserRequest,
  type KeycloakRole,
} from '@/models/domain/keycloak-user'
import { userService, type ListUsersParams } from '@/services/operations/user-service'

const USERS_QUERY_KEY = ['users']
const USER_QUERY_KEY = ['user']
const USER_ROLES_QUERY_KEY = ['user-roles']
const REALM_ROLES_QUERY_KEY = ['realm-roles']

export function useUsers(params?: ListUsersParams) {
  return useQuery({
    queryKey: [...USERS_QUERY_KEY, params],
    queryFn: () => userService.listUsers(params),
    refetchOnMount: 'always',
    staleTime: 5 * 60 * 1000,
  })
}

export function useUser(id: string) {
  return useQuery({
    queryKey: [...USER_QUERY_KEY, id],
    queryFn: () => userService.getUser(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}

export function useCreateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CreateUserRequest) => userService.createUser(request),
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: USERS_QUERY_KEY,
        type: 'all',
      })
    },
  })
}

export function useUpdateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: UpdateUserRequest }) =>
      userService.updateUser(id, request),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: [...USER_QUERY_KEY, variables.id] })
    },
  })
}

export function useUserRoles(id: string) {
  return useQuery({
    queryKey: [...USER_ROLES_QUERY_KEY, id],
    queryFn: () => userService.getUserRoles(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}

export function useAssignRoles() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, roles }: { id: string; roles: KeycloakRole[] }) =>
      userService.assignRoles(id, roles),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: [...USER_QUERY_KEY, variables.id] })
      void queryClient.invalidateQueries({ queryKey: [...USER_ROLES_QUERY_KEY, variables.id] })
    },
  })
}

export function useRemoveRoles() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, roles }: { id: string; roles: KeycloakRole[] }) =>
      userService.removeRoles(id, roles),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: [...USER_QUERY_KEY, variables.id] })
      void queryClient.invalidateQueries({ queryKey: [...USER_ROLES_QUERY_KEY, variables.id] })
    },
  })
}

export function useRealmRoles() {
  return useQuery({
    queryKey: REALM_ROLES_QUERY_KEY,
    queryFn: () => userService.getRealmRoles(),
    staleTime: 5 * 60 * 1000,
  })
}
